"use client"

import React, { useEffect, useState } from "react"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import { loadProduct } from "@/actions/productActions"
import { verifyReCaptcha } from "@/actions/reCaptchaActions"
import { useTranslations } from "next-intl"
import { renderToStaticMarkup } from "react-dom/server"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { SubmitHandler, useForm, useWatch } from "react-hook-form"
import { MARK_BOLD, MARK_LINK, NODE_PARAGRAPH, render, StoryblokRichtext } from "storyblok-rich-text-react-renderer"
import { z } from "zod"

import { sendContactEmail } from "@/lib/email/smtp-client"
import { resolveStoryblokStoryHref } from "@/lib/link-utils/storyblok-link-resolver"
import { isDevMode, isProdMode } from "@/lib/nodeHelper"
import { zodResolver } from "@/lib/polyfill/zodResolver"
import { ReducedProductData } from "@/lib/storefront/product/product"
import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@/components/ui/components"
import { renderRichTextChildrenOrBreak } from "@/components/ui/helpers/richtextRenderer"
import { cn } from "@/components/ui/helpers/utils"
import EmailTemplateContact from "@/components/email-templates/EmailTemplateContact"
import ImageWithFallback from "@/components/storyblok/common/ImageWithFallback"
import { AssetStoryblok, MultilinkStoryblok, RichtextStoryblok } from "@/components/storyblok/component-types"

const MESSAGE_MAX_LENGTH = 30000
const DEV_INSERT_DEFAULTS = isDevMode()
const DEV_SEND_EMAILS = isProdMode()
const DEV_SEND_ONLY_EXTERNAL_EMAIL = !isProdMode()
const DEV_EMAIL = process.env.NEXT_PUBLIC_DEV_EMAIL

export type QuoteRequestProductSummary = {
  sku: string
  title: string
  link: string
  imageUrl: string
  imageWidth: number
  imageHeight: number
  attributes: Array<{ name: string; value: string }>
}

function formatAttributeValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(", ")
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value)
  }
  return ""
}

function mapVariationAttributes(product: ReducedProductData | undefined) {
  return (
    product?.variableVariationAttributes
      ?.map((attribute) => ({
        name: attribute.name ?? attribute.variationAttributeId ?? "",
        value: formatAttributeValue(attribute.value),
      }))
      .filter((attribute) => attribute.name && attribute.value) ?? []
  )
}

function mapProductSummary(product: ReducedProductData | undefined, sku: string): QuoteRequestProductSummary {
  return {
    sku,
    title: product?.title ?? "",
    link: product?.link ?? "",
    imageUrl: product?.small_image?.url || product?.preview_image?.url || product?.image || "",
    imageWidth: product?.small_image?.width || product?.preview_image?.width || product?.image_width || 0,
    imageHeight: product?.small_image?.height || product?.preview_image?.height || product?.image_height || 0,
    attributes: mapVariationAttributes(product),
  }
}

interface UbQuoteRequestFormClientProps {
  sbLocale: string
  sku: string
  product: QuoteRequestProductSummary
  fromEmail: string
  toEmail: string
  emailSubject: string
  emailImage?: AssetStoryblok
  emailHtmlBody: RichtextStoryblok
  emailTextBody: string
  emailFooterLeft: RichtextStoryblok
  emailFooterRight: RichtextStoryblok
  emailFooterBottom: RichtextStoryblok
  title?: RichtextStoryblok
  description?: RichtextStoryblok
  legalConsentNotification?: RichtextStoryblok
  captchaHint?: RichtextStoryblok
  httpRedirect: MultilinkStoryblok | string
  httpRedirectError: MultilinkStoryblok | string
}

function resolveLink(link: MultilinkStoryblok | string | undefined, sbLocale: string): string {
  if (!link) {
    return ""
  }
  if (typeof link === "string") {
    return link
  }
  if (link.linktype === "story" && link.cached_url) {
    return resolveStoryblokStoryHref(link.cached_url, sbLocale, link.story)
  }
  if (link.linktype === "url" && link.url) {
    return link.url
  }
  return ""
}

function renderedRichText(richtext: StoryblokRichtext | unknown, sbLocale: string) {
  return render(richtext, {
    markResolvers: {
      [MARK_BOLD]: (children) => <span className="font-bold">{children}</span>,
      [MARK_LINK]: (children, { linktype, href, target }) => {
        if (linktype === "story" && href) {
          return (
            <Link
              prefetch={false}
              className="text-primary"
              href={resolveStoryblokStoryHref(href, sbLocale)}
              target={target}
            >
              {children}
            </Link>
          )
        }
        if (linktype === "url" && href) {
          return (
            <a className="text-primary" href={href} target={target} rel={target === "_blank" ? "noreferrer" : undefined}>
              {children}
            </a>
          )
        }
        return <>{children}</>
      },
    },
    nodeResolvers: {
      [NODE_PARAGRAPH]: (children) => <>{renderRichTextChildrenOrBreak(children)}</>,
    },
  })
}

export default function UbQuoteRequestFormClient(props: UbQuoteRequestFormClientProps) {
  const t = useTranslations("storefront")
  const router = useRouter()
  const searchParams = useSearchParams()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [sending, setSending] = useState(false)
  const initialSku = props.sku || searchParams.get("SKU") || searchParams.get("sku") || props.product.sku
  const [product, setProduct] = useState<QuoteRequestProductSummary>(props.product)
  const effectiveSku = props.sku || searchParams.get("SKU") || searchParams.get("sku") || product.sku
  const productLoading = Boolean(effectiveSku && !product.title && product.sku !== effectiveSku)

  const formSchema = z.object({
    QuoteRequestForm_Title: z.enum(["salutation.ms", "salutation.mr", "salutation.divers"], {
      error: (issue) => (issue.input === undefined ? undefined : undefined),
    }),
    QuoteRequestForm_Name: z
      .string()
      .min(2, t("forms.catalogue_order_form.error_name"))
      .max(64, t("forms.contact_us_generic_form.error.first_name_max")),
    QuoteRequestForm_Company: z
      .string()
      .min(2, t("forms.catalogue_order_form.error_company"))
      .max(64, t("forms.contact_us_generic_form.error.company_name_max")),
    QuoteRequestForm_Phone: z
      .string()
      .min(2, t("forms.catalogue_order_form.error_phone"))
      .max(32, t("forms.contact_us_generic_form.error.phone_max")),
    QuoteRequestForm_Email: z.email(t("forms.catalogue_order_form.error_email")),
    QuoteRequestForm_Street: z
      .string()
      .min(2, t("forms.catalogue_order_form.error_street"))
      .max(100, t("forms.contact_us_generic_form.error.street_max")),
    QuoteRequestForm_ZIP: z
      .string()
      .min(2, t("forms.catalogue_order_form.error_zip"))
      .max(10, t("forms.contact_us_generic_form.error.postal_code_max")),
    QuoteRequestForm_City: z
      .string()
      .min(2, t("forms.catalogue_order_form.error_city"))
      .max(100, t("forms.contact_us_generic_form.error.city_max")),
    QuoteRequestForm_Country: z
      .string()
      .min(2, t("forms.catalogue_order_form.error_country"))
      .max(50, t("forms.catalogue_order_form.error_max")),
    QuoteRequestForm_Quantity: z.string().max(50, t("forms.catalogue_order_form.error_max")).optional(),
    QuoteRequestForm_Message: z.string().max(MESSAGE_MAX_LENGTH, t("forms.quote_request_form.error.message_max")),
    QuoteRequestForm_AcceptTerms: z.literal<boolean>(true, {
      error: () => t("forms.catalogue_order_form.error_terms"),
    }),
  })

  type FormSchemaType = z.infer<typeof formSchema>

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      QuoteRequestForm_Title: undefined,
      QuoteRequestForm_Name: DEV_INSERT_DEFAULTS ? "John Doe" : "",
      QuoteRequestForm_Company: DEV_INSERT_DEFAULTS ? "ACME Company" : "",
      QuoteRequestForm_Phone: DEV_INSERT_DEFAULTS ? "123456" : "",
      QuoteRequestForm_Email: DEV_INSERT_DEFAULTS ? DEV_EMAIL : "",
      QuoteRequestForm_Street: DEV_INSERT_DEFAULTS ? "ACME Street" : "",
      QuoteRequestForm_ZIP: DEV_INSERT_DEFAULTS ? "12345" : "",
      QuoteRequestForm_City: DEV_INSERT_DEFAULTS ? "Gotham City" : "",
      QuoteRequestForm_Country: DEV_INSERT_DEFAULTS ? "Deutschland" : "",
      QuoteRequestForm_Quantity: "",
      QuoteRequestForm_Message: "",
      QuoteRequestForm_AcceptTerms: DEV_INSERT_DEFAULTS,
    },
  })

  const successRedirectLink = resolveLink(props.httpRedirect, props.sbLocale)
  const errorRedirectLink = resolveLink(props.httpRedirectError, props.sbLocale)
  const message = useWatch({ control: form.control, name: "QuoteRequestForm_Message" }) ?? ""

  useEffect(() => {
    if (!effectiveSku) {
      return
    }

    if (product.sku === effectiveSku) {
      return
    }

    let cancelled = false
    loadProduct(effectiveSku, props.sbLocale)
      .then((loadedProduct) => {
        if (!cancelled) {
          setProduct(mapProductSummary(loadedProduct, effectiveSku))
        }
      })
      .catch((error) => {
        console.log(`UbQuoteRequestFormClient: error loading product ${effectiveSku}: ${error}`)
      })

    return () => {
      cancelled = true
    }
  }, [effectiveSku, product.sku, product.title, props.sbLocale])

  const sanitizeField = (text: string): string => {
    return text.replaceAll("\r\n", "\\n").replaceAll("\n", "\\n").replaceAll('"', '\\"')
  }

  const getProductAttributesText = () =>
    product.attributes.map((attribute) => `${attribute.name}: ${attribute.value}`).join("\\n")

  const getPrefixedProductSku = () => {
    const sku = product.sku.trim()
    return sku && !sku.toUpperCase().startsWith("A") && !sku.toUpperCase().startsWith("E") ? `A${sku}` : sku
  }

  const getProductUrl = () => {
    if (!product.link) {
      return ""
    }
    if (product.link.startsWith("http")) {
      return product.link
    }
    return `${window.location.origin}${product.link}`
  }

  const replacePlaceholder = (text: string, value: FormSchemaType): string => {
    return text
      .replaceAll("${Title}", sanitizeField(t(value.QuoteRequestForm_Title ?? "")))
      .replaceAll("${Name}", sanitizeField(value.QuoteRequestForm_Name))
      .replaceAll("${Company}", sanitizeField(value.QuoteRequestForm_Company))
      .replaceAll("${Phone}", sanitizeField(value.QuoteRequestForm_Phone))
      .replaceAll("${Email}", sanitizeField(value.QuoteRequestForm_Email))
      .replaceAll("${Street}", sanitizeField(value.QuoteRequestForm_Street))
      .replaceAll("${ZIP}", sanitizeField(value.QuoteRequestForm_ZIP))
      .replaceAll("${PostalCode}", sanitizeField(value.QuoteRequestForm_ZIP))
      .replaceAll("${City}", sanitizeField(value.QuoteRequestForm_City))
      .replaceAll("${Country}", sanitizeField(value.QuoteRequestForm_Country))
      .replaceAll("${Quantity}", sanitizeField(value.QuoteRequestForm_Quantity ?? ""))
      .replaceAll("${Message}", sanitizeField(value.QuoteRequestForm_Message))
      .replaceAll("${ProductSku}", sanitizeField(product.sku))
      .replaceAll("${ProductSkuPrefixed}", sanitizeField(getPrefixedProductSku()))
      .replaceAll("${ProductArticleNumber}", sanitizeField(getPrefixedProductSku()))
      .replaceAll("${ProductName}", sanitizeField(product.title))
      .replaceAll("${ProductUrl}", sanitizeField(getProductUrl()))
      .replaceAll("${ProductAttributes}", sanitizeField(getProductAttributesText()))
  }

  const getEmailSubject = (value: FormSchemaType) => replacePlaceholder(props.emailSubject, value)
  const getEmailTextBody = (value: FormSchemaType) => replacePlaceholder(props.emailTextBody, value)
  const getEmailHtmlBody = (value: FormSchemaType) => {
    const richtextReplaced = replacePlaceholder(JSON.stringify(props.emailHtmlBody), value)
    const body = JSON.parse(richtextReplaced)

    return renderToStaticMarkup(
      <EmailTemplateContact
        sbLocale={props.sbLocale}
        body={body}
        image={props.emailImage}
        footerLeft={props.emailFooterLeft}
        footerRight={props.emailFooterRight}
        footerBottom={props.emailFooterBottom}
      />
    )
  }

  const redirect = (succeeded: boolean) => {
    try {
      const target = succeeded ? successRedirectLink : errorRedirectLink
      target ? router.push(target) : router.back()
    } catch (error) {
      console.log(`UbQuoteRequestFormClient: error on redirecting: ${error}`)
    }
  }

  const sendEmails = (value: FormSchemaType) => {
    const emailSubject = getEmailSubject(value)
    const emailTextBody = getEmailTextBody(value)
    const emailHtmlBody = getEmailHtmlBody(value)

    const externalEmail = () =>
      sendContactEmail({
        from: props.fromEmail,
        to: value.QuoteRequestForm_Email,
        subject: emailSubject,
        text: emailTextBody,
        html: emailHtmlBody,
      })

    const internalEmail = () =>
      sendContactEmail({
        from: props.fromEmail,
        to: props.toEmail,
        subject: emailSubject,
        text: emailTextBody,
        html: emailHtmlBody,
      })

    if (DEV_SEND_EMAILS && props.fromEmail && props.toEmail && props.emailSubject) {
      Promise.all([externalEmail(), internalEmail()])
        .then(() => redirect(true))
        .catch((error) => {
          console.log(`UbQuoteRequestFormClient: error sending emails: ${error}`)
          redirect(false)
        })
    } else if (DEV_SEND_ONLY_EXTERNAL_EMAIL && props.fromEmail && props.emailSubject) {
      externalEmail()
        .then(() => redirect(true))
        .catch((error) => {
          console.log(`UbQuoteRequestFormClient: error sending external email: ${error}`)
          redirect(false)
        })
    } else {
      console.log("UbQuoteRequestFormClient: email delivery disabled or email config missing")
      redirect(false)
    }
  }

  const onSubmit: SubmitHandler<FormSchemaType> = (data, event) => {
    event && event.preventDefault()
    const action = "submit_quote_request_form"
    setSending(true)

    if (executeRecaptcha) {
      executeRecaptcha(action)
        .then((token) => {
          verifyReCaptcha(token, action, product.sku)
            .then((result) => {
              result ? sendEmails(data) : redirect(false)
            })
            .catch((error) => {
              console.log(`UbQuoteRequestFormClient: error verifying reCaptcha: ${error.message}`)
              redirect(false)
            })
        })
        .catch((error) => {
          console.log(`UbQuoteRequestFormClient: error executing recaptcha: ${error.message}`)
          redirect(false)
        })
    } else {
      console.log("UbQuoteRequestFormClient: execute recaptcha not available yet")
      redirect(false)
    }
  }

  const required = <span className="ml-1 text-red-500">*</span>

  return (
    <section
      className={cn("mx-auto w-full max-w-5xl px-4 py-8 md:px-6 lg:py-12", {
        "pointer-events-none cursor-wait opacity-30": sending,
      })}
    >
      <div className="mb-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(320px,2fr)]">
        <div>
          {props.title ? (
            <div className="mb-4 text-3xl font-bold">{renderedRichText(props.title, props.sbLocale)}</div>
          ) : (
            <h1 className="mb-4 text-3xl font-bold">{t("forms.quote_request_form.title")}</h1>
          )}
          <div className="text-muted-foreground text-sm/relaxed">
            {props.description
              ? renderedRichText(props.description, props.sbLocale)
              : t("forms.quote_request_form.description")}
          </div>
        </div>
        <div className="grid gap-5 rounded-sm border border-gray-200 bg-white p-4 sm:grid-cols-[140px_minmax(0,1fr)]">
          <div className="flex aspect-square items-center justify-center bg-gray-50">
            {productLoading ? (
              <div className="h-24 w-20 animate-pulse rounded-sm bg-gray-200" />
            ) : product.imageUrl ? (
              <ImageWithFallback
                className="max-h-full max-w-full object-contain"
                src={product.imageUrl}
                width={product.imageWidth || 140}
                height={product.imageHeight || 140}
                alt={product.title || product.sku}
              />
            ) : (
              <span className="text-muted-foreground px-3 text-center text-sm">
                {t("forms.quote_request_form.no_product_image")}
              </span>
            )}
          </div>
          <div className="min-w-0 text-sm">
            {productLoading ? (
              <div className="space-y-3 py-1">
                <div className="h-4 w-40 animate-pulse rounded-sm bg-gray-200" />
                <div className="h-4 w-full max-w-md animate-pulse rounded-sm bg-gray-200" />
                <div className="h-4 w-56 animate-pulse rounded-sm bg-gray-200" />
              </div>
            ) : (
              <>
                <p className="font-semibold">
                  {t("product.article_number")}: {product.sku || effectiveSku || t("forms.quote_request_form.no_sku")}
                </p>
                {product.title ? (
                  <p className="text-muted-foreground mt-1">{product.title}</p>
                ) : (
                  <p className="text-muted-foreground mt-1">{t("forms.quote_request_form.product_not_found")}</p>
                )}
                {product.attributes.length > 0 && (
                  <dl className="mt-3 grid gap-1">
                    {product.attributes.map((attribute) => (
                      <div
                        key={`${attribute.name}-${attribute.value}`}
                        className="grid gap-1 sm:grid-cols-[140px_minmax(0,1fr)]"
                      >
                        <dt className="font-medium">{attribute.name}</dt>
                        <dd className="text-muted-foreground min-w-0 wrap-break-word">{attribute.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <fieldset className="grid grid-cols-1 gap-5 lg:grid-cols-12">
            <input type="hidden" name="QuoteRequestForm_SKU" value={product.sku || effectiveSku} />
            <input type="hidden" name="QuoteRequestForm_ProductName" value={product.title} />

            <FormField
              control={form.control}
              name="QuoteRequestForm_Title"
              render={({ field }) => (
                <FormItem className="lg:col-span-4">
                  <FormLabel className="font-bold">
                    {t("forms.catalogue_order_form.label_title")}
                    {required}
                  </FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("forms.catalogue_order_form.select_title")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="salutation.ms">{t("salutation.ms")}</SelectItem>
                      <SelectItem value="salutation.mr">{t("salutation.mr")}</SelectItem>
                      <SelectItem value="salutation.divers">{t("salutation.divers")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="QuoteRequestForm_Name"
              render={({ field }) => (
                <FormItem className="lg:col-span-4">
                  <FormLabel className="font-bold">
                    {t("forms.catalogue_order_form.label_name")}
                    {required}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="QuoteRequestForm_Company"
              render={({ field }) => (
                <FormItem className="lg:col-span-4">
                  <FormLabel className="font-bold">
                    {t("forms.catalogue_order_form.label_company")}
                    {required}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="QuoteRequestForm_Phone"
              render={({ field }) => (
                <FormItem className="lg:col-span-6">
                  <FormLabel className="font-bold">
                    {t("forms.catalogue_order_form.label_phone")}
                    {required}
                  </FormLabel>
                  <FormControl>
                    <Input type="tel" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="QuoteRequestForm_Email"
              render={({ field }) => (
                <FormItem className="lg:col-span-6">
                  <FormLabel className="font-bold">
                    {t("forms.catalogue_order_form.label_email")}
                    {required}
                  </FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="QuoteRequestForm_Street"
              render={({ field }) => (
                <FormItem className="lg:col-span-3">
                  <FormLabel className="font-bold">
                    {t("forms.catalogue_order_form.label_street")}
                    {required}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="QuoteRequestForm_ZIP"
              render={({ field }) => (
                <FormItem className="lg:col-span-3">
                  <FormLabel className="font-bold">
                    {t("forms.catalogue_order_form.label_zip")}
                    {required}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="QuoteRequestForm_City"
              render={({ field }) => (
                <FormItem className="lg:col-span-3">
                  <FormLabel className="font-bold">
                    {t("forms.catalogue_order_form.label_city")}
                    {required}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="QuoteRequestForm_Country"
              render={({ field }) => (
                <FormItem className="lg:col-span-3">
                  <FormLabel className="font-bold">
                    {t("forms.catalogue_order_form.label_country")}
                    {required}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="QuoteRequestForm_Quantity"
              render={({ field }) => (
                <FormItem className="lg:col-span-3">
                  <FormLabel className="font-bold">{t("forms.quote_request_form.label_quantity")}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="QuoteRequestForm_Message"
              render={({ field }) => (
                <FormItem className="lg:col-span-12">
                  <FormLabel className="font-bold">{t("forms.quote_request_form.label_message")}</FormLabel>
                  <FormControl>
                    <Textarea className="min-h-48" maxLength={MESSAGE_MAX_LENGTH} {...field} />
                  </FormControl>
                  <p className="text-muted-foreground text-xs">
                    {MESSAGE_MAX_LENGTH - message.length} {t("forms.quote_request_form.characters_remaining")}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="QuoteRequestForm_AcceptTerms"
              render={({ field }) => (
                <FormItem className="lg:col-span-12">
                  <div className="flex items-start gap-3">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="text-sm/relaxed font-normal">
                      {props.legalConsentNotification
                        ? renderedRichText(props.legalConsentNotification, props.sbLocale)
                        : t("forms.quote_request_form.legal_consent")}
                      {required}
                    </FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </fieldset>

          <div className="mt-6">
            <Button type="submit" size="sm" disabled={sending}>
              {t("forms.catalogue_order_form.send")}
            </Button>
          </div>
          <p className="text-muted-foreground mt-6 text-sm">
            {props.captchaHint
              ? renderedRichText(props.captchaHint, props.sbLocale)
              : t("forms.quote_request_form.captcha_hint")}
          </p>
        </form>
      </Form>
    </section>
  )
}
